//#region Imports and Global Definitions
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StyleSheet, View, SafeAreaView, Platform, useWindowDimensions, Pressable, FlatList, TouchableOpacity } from 'react-native';
import { useTheme } from '@react-navigation/native';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import { createCommonStyles } from '../../../styles/commonStyles';
import { ActivityIndicator, Card } from 'react-native-paper';
import { showMessage } from "react-native-flash-message";
import { useTableHelper } from '../../../hooks/useTableHelper';
import { useTranslation } from 'react-i18next';
import { useTraining } from '../../../hooks/useTraining';
import Ionicons from "@react-native-vector-icons/ionicons/static";
import { generateUniqueID } from '../../../components/SurveyBuilderComponent/CreateSurvey/CreateSurveyFunctions';
//#endregion

const SubjectListView = React.memo(({ item, index, styles, handleEditPress, handleDeleteSubjects
 }) => {

    return (
        <SafeAreaView>
            <SafeAreaView style={styles.row}>
                <View style={[styles.item]}>
                    <CustomInput
                        value={item.subject}
                        setValue={(e) => { handleEditPress(index, item, 'subject', e) }}
                        maxLen={10}
                        type="MAININPUT"
                    />
                </View>
                <View style={[styles.item]}>
                    <CustomInput
                        value={item.description}
                        setValue={(e) => { handleEditPress(index, item, 'description', e) }}
                        maxLen={50}
                        type="MAININPUT"
                    />
                </View>
                <CustomButton
                    iconName='trash-outline'
                    onPress={() => { handleDeleteSubjects(item, index, 'rowguid') }}
                    show='HOME_BELL'
                    type='HOME_BELL'
                    textTop='2'
                    sz={30} />
            </SafeAreaView>
        </SafeAreaView>
    );
});

const HRAdminSubjectScreen = (props) => {
    //#region Props
    const { data, selectedData, goBack } = props;
    //#endregion

    //#region Const Definitions
    const ref = useRef(null);
    const { t, i18n } = useTranslation();
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const [isLoading, setIsLoading] = useState(true);

    const { updateSubjects, insertSubjects, deleteSubjects, loadSubjects } = useTraining();
    const [refreshAfterSave, setRefreshAfterSave] = useState(false);
    const { rowEdit, rowDelete, rowAdd, handleCancelChanges, hasDuplicates } = useTableHelper();

    const [subjectData, setSubjectData] = useState([]);
    const [originalData, setOriginalData] = useState(data);
    const [changes, setChanges] = useState({});
    const [selectedCategory, setSelectedCategory] = useState(selectedData);
    const [insertData, setInsertData] = useState([]);
    const [deletedItems, setDeletedItems] = useState([]);
    const [duplicates, setDuplicates] = useState([]);
    //#endregion

    useEffect(() => {
        setIsLoading(true);
        // Load Subjects
        loadSubjects().then((subjects) => {
            const filteredSubjects = subjects.filter(subj => subj.category === selectedCategory);
            setChanges({});
            setOriginalData([...filteredSubjects]);
            setSubjectData(filteredSubjects);
            setSelectedCategory(selectedData)
            setRefreshAfterSave(false);
            setIsLoading(false);
        })
        .catch(() => {
            setRefreshAfterSave(false);
            setIsLoading(false);
        });
        setRefreshAfterSave(false);
        setIsLoading(false);
    },[refreshAfterSave])

    //#region Components
    const renderData = (item, index) => {
        return (
            <SubjectListView item={item} index={index}
                styles={styles}
                handleEditPress={handleEditPress}
                handleDeleteSubjects={handleDeleteSubjects}
                insertData={insertData}
            />
        )
    }

    const renderSubjects = useCallback((item, index) => {
        return (
            <Card
                style={[common.cardstyle, { margin: 10 }]} >
                <Card.Content>
                    {renderData(item, index)}
                </Card.Content>
            </Card >
        )
    }, [subjectData]);
    //#endregion

    //#region Handlers
    const handleEditPress = useCallback((index, item, key, value) => {
        if (item[key] === value) return;
        rowEdit(index, item, key, value, changes, setChanges, subjectData, setSubjectData)
        rowEdit(index, item, 'category', selectedCategory, changes, setChanges, subjectData, setSubjectData)
    }, []);

    const handleDeleteSubjects = (item, index, attribute) => {
        rowDelete(item, index, attribute, changes, setChanges, subjectData, setSubjectData, setDeletedItems, ref)
    }

    const handleAddNewSubject = () => {
        let temp = [...subjectData];
        if (subjectData.length === 0)
            temp = {
                category: "",
                subject: "",
                description: "",
                rowguid: generateUniqueID()
            }
        rowAdd(temp, setSubjectData, ['rowguid'], [], ref, t, [], [], undefined, undefined, insertData, setInsertData, id = 'rowguid',);
    }

    const removeFromChangesAfterSave = () => {
        setChanges([])
        setDeletedItems([])
    }

    const validateInput = () => {
        let ret = [true, t('common:validationSuccess')]
        subjectData.forEach((itr) => {
            if (itr.subject === '')
                ret = [false, t('common:mandatoryCodeNotGivenCategory')];
            if (itr.description === '')
                ret = [false, t('common:descEmpty')]

        })
        return (ret);
    }

    const handleSave = async () => {
        let newPromises = [];
        let deleteSucc = true;
        let insertSucc = true;
        let updateSucc = true;
    
        if (Object.entries(changes).length == 0 && deletedItems.length == 0) {
            showMessage({
                message: t('common:noChanges'),
                type: "warning",
                duration: 3000,
            })
            return;
        }
    
        let valid = true;
        const attributesToIgnore = ['category', 'description'];
    
        if (subjectData.length > 0)
            Object.keys(subjectData[0]).forEach(key => {
                //* Ignore Keys that we know can be duplicated 
                if (!attributesToIgnore.includes(key)) {
                    if (hasDuplicates(subjectData, key, setDuplicates)) {
                        showMessage({
                            message: t('common:duplicates'),
                            type: "danger",
                            duration: 3000,
                        });
                        valid = false;
                    }
                }
            });
    
        if (!valid) return null;
    
        let valRet = validateInput();
        if (!valRet[0]) {
            console.log(valRet[1])
            showMessage({
                message: valRet[1],
                type: "danger",
                duration: 3000,
            })
            return;
        }
        setIsLoading(true);
        let dataForInsert = [];
        let dataForUpdate = [];

        Object.entries(subjectData).forEach(([key, obj]) => {
            if (insertData.includes(obj.rowguid)) {
                dataForInsert.push(obj);
            } else {
                dataForUpdate.push(obj);
            }
        });

        if (Object.keys(dataForUpdate).length > 0) {
            newPromises.push(updateSubjects(dataForUpdate)
            .then((res) => {
                if (res !== 200){
                    updateSucc = false;
                } else {
                    removeFromChangesAfterSave()
                }
            })
            .catch(() => {}));
        }

        if (Object.keys(dataForInsert).length > 0) {
            newPromises.push(insertSubjects(dataForInsert)
            .then((res) => {
                if (res !== 200) insertSucc = false;
                removeFromChangesAfterSave()
            })
            .catch(() => { }));
        }

        if (deletedItems.length > 0) {
            newPromises.push(deleteSubjects(deletedItems).then((res) => {
                if (res !== 200) deleteSucc = false;
                removeFromChangesAfterSave()
            })
            .catch(() => { }));
        }
    
        try {
            await Promise.all(newPromises).then(() => {
                setIsLoading(false);
                if (!insertSucc || !deleteSucc || !updateSucc) {
                    if (!deleteSucc) {
                        showMessage({
                            message: t('common:functionWithServLinesDeleteMess'),
                            type: "danger",
                            duration: 3000,
                        });
                        let removeDeleteBranches = Object.fromEntries(Object.entries(changes).filter(([key, value]) =>
                            value.isDeleted !== true)
                        )
                        setChanges(removeDeleteBranches);
                    } else {
                        showMessage({
                            message: t('common:issueMessage'),
                            type: "error",
                            duration: 3000,
                        })
                    }
                } else if (insertSucc && deleteSucc && updateSucc) {
                    console.log('here')
                    showMessage({
                        message: t('common:allOperationsSuccMess'),
                        type: "success",
                        duration: 3000,
                    });
                    setDuplicates([]);
                    setRefreshAfterSave(true);
                }
            });
        } catch (error) {
            setRefreshAfterSave(true);
            setDuplicates([]);
        }
    }

    const handleGoBack = () => {
        props.goBack();
    }
    //#endregion

    //#region Loading
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion

    return (
        <SafeAreaView style={styles.mainView}>
            <TouchableOpacity onPress={() => { handleGoBack() }} style={styles.backButton}>
                <Ionicons name="arrow-back" size={30} color="black" />
            </TouchableOpacity>
            <SafeAreaView style={styles.rowView}>
                <SafeAreaView style={[styles.subMain]}>
                    <SafeAreaView style={styles.innerView}>
                        <SafeAreaView style={[styles.buttons, styles.functionalityButtons]}>
                            <CustomButton
                                text={t('common:new')}
                                onPress={() => { handleAddNewSubject() }}
                                type='BUTTON_ADD'
                                textTop='0'
                                sz={15}
                            />
                            <CustomButton
                                text={t('common:save')}
                                onPress={() => { handleSave() }}
                                type='BUTTON_SAVE'
                                textTop='0'
                                sz={15}
                                isGradient={true}
                            />
                            <CustomButton
                                text={t('common:cancel')}
                                onPress={() => { 
                                    handleCancelChanges(setSubjectData, originalData, setDeletedItems, setChanges);
                                }}
                                type="BUTTON_CANCEL"
                                textTop='0'
                                sz={15}
                            />
                        </SafeAreaView>
                        <SafeAreaView style={styles.functionsView}>
                            <FlatList
                                style={styles.fullWidth}
                                data={subjectData}
                                keyExtractor={(item, index) => `${item.subject}-${index}`}
                                renderItem={({ item, index }) =>
                                    renderSubjects(item, index)
                                }
                                scrollEnabled={true}
                                refreshing={isLoading}
                                ref={ref}
                                ListHeaderComponent={
                                    <SafeAreaView>
                                        <View style={[styles.row, { marginRight: '3%', }]}>
                                            <CustomLabel
                                                value={t('common:subject')}
                                                type='SUBTITLE' />
                                            <CustomLabel
                                                value={t('common:description')}
                                                type='SUBTITLE' />
                                        </View>
                                    </SafeAreaView>
                                }
                                ListEmptyComponent={
                                    isLoading ?
                                        <ActivityIndicator animating={true} color={colors.blue} />
                                        :
                                        <CustomLabel
                                            value={t("common:NoDataPreview")}
                                            type="NO_FUNCTIONS"
                                        />
                                }
                                initialNumToRender={4}
                            />
                        </SafeAreaView>
                    </SafeAreaView>
                </SafeAreaView>
            </SafeAreaView>
        </SafeAreaView>
    );
};

//#region Styles
const makeStyles = (colors, width) => StyleSheet.create({
    mainView: {
        flex: 1,
    },
    subMain: {
        borderRadius: 35,
        elevation: (Platform.OS === 'web' && width > 992 ? 3 : 0),
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
        width: '100%'
    },
    rowView: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center'
    },
    cardStyleFunctions: {
        margin: 10,
        borderRadius: 5,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
    },
    item: {
        flex: 1,
    },
    error: {
        color: colors.validation_error,
        alignSelf: 'center',
        fontSize: 14,
    },
    selectedButton: {
        margin: 10,
        borderRadius: 5,
        borderWidth: 2,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
        borderColor: colors.buttons_submit,
    },
    backButton: {
        alignSelf: 'flex-end',
        margin: 0
    },
    innerView: {
        flex: 1,
    },
    functionsView: {
        flexDirection: 'row',
        height: '100%'
    },
    fullHeight: {
        height: '95%',
    },
    fullWidth: {
        height: '85%',
    },
    servLinesView: {
        width: '85%',
        height: '100%',
    },
    functionalityButtons: {
        justifyContent: 'flex-end'
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        width: '50%',
        alignSelf: 'flex-end',
    },
    loader: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4%'
    },
});
//#endregion

export default HRAdminSubjectScreen;